import * as React from 'react';
import { getLocalStorage } from '../../utils';
import { AuthContext } from './auth-provider.context';
import { checkIsVizitEmail, useAuthentication } from './auth-provider.hooks';
import { authStateReducer } from './auth-provider.state';

interface AuthProviderProps {
  /**
   * The children of the AuthProvider.
   *
   * @required
   * @type {ReactNode}
   */
  children: React.ReactNode;
}

/**
 * The AuthProvider component is used to provide the auth state to the app.
 * It is used to authenticate the user and to change the organization view based on
 * the attached JWT token. The attached JWT is stored in an HTTP only cookie. Organization
 * ID and User ID will be stored in localStorage to persist the auth state across page
 * reloads.
 *
 * @param props - The props for the AuthProvider component.
 * @returns The AuthProvider component.
 */
function AuthProvider(props: AuthProviderProps) {
  const { children } = props;

  // Check localStorage for organizationId and userId. If they exist, then the user already has a JWT token stored in cookies.
  const {
    banner,
    organization_id: organizationId,
    user_id: userId,
    organization_name: organizationName,
    user_email: userEmail,
    default_portfolio_id: defaultPortfolioId,
    default_retailer: defaultRetailer,
  } = getLocalStorage([
    'organization_id',
    'user_id',
    'user_email',
    'default_portfolio_id',
    'default_retailer',
    'banner',
  ]);

  const [authState, dispatch] = React.useReducer(authStateReducer, {
    isAuthenticated: (!!organizationId && !!userId) || false,
    isVizitUser: !!userId && !!userEmail && checkIsVizitEmail(userEmail),
    isDisplayingBanner: banner !== undefined ? banner === 'true' : undefined,
    organizationId,
    organizationName,
    defaultPortfolioId,
    defaultRetailer,
    userId,
  });

  // For user authentication only.
  const { onAuthenticate, onRemoveAuthentication, onReauthenticate, onSetPassword, onExchangeToken, onDismissBanner } =
    useAuthentication({
      authState,
      dispatch,
    });

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        onAuthenticate,
        onRemoveAuthentication,
        onReauthenticate,
        onSetPassword,
        onExchangeToken,
        onDismissBanner,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuth };
